/**
 * The four counts that open the view each one belongs to.
 *
 * Each card is the control: a count a reader cannot act on is a dead end, and
 * the four views below are exactly where the four numbers come from.
 *
 * @module components/portfolio/comparison/ComparisonSummary
 */

import React from "react";

import { Divider, Icon, StatCard } from "@/components/ui";

export interface ComparisonMetric {
  key: string;
  label: string;
  value: number;
  /** Opens the view this count came from. */
  onOpen: () => void;
}

export interface ComparisonSummaryProps {
  metrics: readonly ComparisonMetric[];
}

/**
 * @example
 * ```tsx
 * <ComparisonSummary metrics={[{ key: "changes", label: "Changes recorded", value: 12, onOpen }]} />
 * ```
 */
export function ComparisonSummary({ metrics }: ComparisonSummaryProps): React.JSX.Element {
  return (
    <div data-testid="comparison-summary">
      <Divider />
      <div className="grid gap-4 py-5 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric) => (
          // The button wraps the card rather than sitting inside it: the whole
          // card is the target, and `StatCard` renders nothing focusable of its
          // own unless it is given `help`, which none of these are.
          <button
            key={metric.key}
            type="button"
            className="group rounded-box text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            onClick={metric.onOpen}
            // No `aria-label`: it overrides the content, and the figure is what
            // the card exists to report. The card's own text carries the label,
            // the number, and the "Open" that says the card is a control.
            data-testid={`comparison-metric-${metric.key}`}
          >
            {/* The "Open" is the card's footnote, so it sits inside the card's
              border and stays with its own card when the grid stacks. */}
            <StatCard
              inline
              label={metric.label}
              value={metric.value}
              footnote={
                <span className="flex items-center gap-1 font-semibold text-primary-strong">
                  Open
                  <Icon name="arrow-right" aria-hidden />
                </span>
              }
              className="h-full transition-colors group-hover:bg-surface-hover"
            />
          </button>
        ))}
      </div>
      <Divider />
    </div>
  );
}
